import React from 'react';
import Box from '@mui/material/Box';
import styled from '@mui/system/styled';
import theme from '@/mui/theme';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { getIconComponentByName } from '@/utils/icons-map';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Slider from '@mui/material/Slider';
import { idFromString } from '@/utils/miscellaneous';
import { getDefaultSortOrder } from '@/utils/slug';

interface SingleAccordionProps {
  index: number
  accordionData: {
    accordionTitle: string
    accordionCheckbox?: [
      {
        label: string
        value: string
      }
    ]
    accordionSlider?: {
      valueMin?: number
      valueMax?: number
      label?: string
      marks?: [
        {
          value: any,
          label: string
        }
      ]
      marksValues: any[]
      isReset: boolean | undefined
    }
  }
  facets: any;
  storeData: {
    storeId: string
    langId: string
    catalogId: string
    locale: string
    categoryId: string,
    pageNumber: string,
    pageSize: string
  }
  handleFilter: Function
}

interface SliderMarksProps {
  title: string
  valueMin?: number
  valueMax?: number
  label?: string | undefined
  marks?: [
    {
      value: any,
      label: string
    }
  ]
  marksValues: any[]
  isReset: boolean | undefined
  storeData: {
    storeId: string
    langId: string
    catalogId: string
    locale: string
    categoryId: string,
    pageNumber: string,
    pageSize: string
  }
  handleFilter: Function
}

interface TabPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel: React.FC<TabPanelProps> = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-detail-tab-${index}`}
      aria-labelledby={`product-detail-tabpanel-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { sm: '17px 18px', xs: '19px 18px 37px' } }}>
          <Box>{children}</Box>
        </Box>
      )}
    </div>
  )
}

const SliderMarks: React.FC<SliderMarksProps> = ({ title, valueMin = 1, valueMax = 100, label, marks, marksValues, isReset, storeData, handleFilter }) => {
  const [value, setValue] = React.useState<number[]>([valueMin, valueMax]);
  let startServiceFilterCall : any = null;

  const handleChange = (_event: React.SyntheticEvent | Event, newValue: number | number[]) => {
    //console.log('\nevent: ', event);
    let minIndex = 0;
      let maxIndex = 100;
      let filterValueRangeArrayMap : any = {};
      let filterValueRange : any[] = [];
      if(Array.isArray(newValue)) {
        minIndex = marksValues.indexOf(newValue[0]);
        maxIndex = marksValues.indexOf(newValue[1]);
        filterValueRangeArrayMap = marks?.slice(minIndex,maxIndex + 1);
        filterValueRange = filterValueRangeArrayMap?.map((filterValue: any) => filterValue.valueForQuery);
      } else {
        let index = marksValues.indexOf(newValue);
        if(marks)
          filterValueRangeArrayMap = marks[index];
        filterValueRange = filterValueRangeArrayMap?.map((filterValue: any) => filterValue.valueForQuery);
      }
      if(isReset)
        newValue = [valueMin, valueMax];
      setValue(newValue as number[]);
      if(minIndex !== valueMin || maxIndex !== valueMax) {
          if(startServiceFilterCall)
            clearTimeout(startServiceFilterCall);
          startServiceFilterCall = setTimeout(() => {
            let queryParams = {
              storeId: storeData.storeId,
              langId: storeData.langId,
              catalogId: storeData.catalogId,
              locale: storeData.locale,
              categoryId: storeData.categoryId,
              filterValue: filterValueRange,
              pageNumber: storeData.pageNumber,
              pageSize: storeData.pageSize,
              orderBy: getDefaultSortOrder()
            }
            handleFilter(queryParams);
        }, 1000);
      }
  }

  return <Box>
    <StyledSlider
      getAriaLabel={() => title}
      defaultValue={value}
      min={valueMin}
      max={valueMax}
      step={null}
      marks={marks}
      valueLabelDisplay="off"
      onChangeCommitted={handleChange}
      value={value}
    />
    <Box display="flex" justifyContent="space-between">
      <StyledBoxVal>{value[0]}{label}</StyledBoxVal>
      <StyledBoxVal>{value[1]}{label}</StyledBoxVal>
    </Box>
  </Box>
}

const StyledTitle = styled(Typography)({
  color: '#000000',
  fontSize: '16px',
  fontWeight: '800',
  letterSpacing: '0.1px',
  lineHeight: '24px',
  paddingLeft: '6px'
})

const StyledAccordion = styled(Accordion)({
  backgroundColor: '#f1f1f1',
  border: '0',
  boxShadow: 'unset',
  marginBottom: '3px',
  '.MuiAccordionDetails-root': {
    padding: '0',
    backgroundColor: '#ffffff',
  },
  '&::before': {
    display: 'none',
  }
})

const StyledAccordionSummary = styled(AccordionSummary)({
  padding: '0 20px',
  "&.Mui-expanded .Mui-expanded": {
    transform: "rotate(0deg)",
  },
})

const StyledTabPanel = styled(TabPanel)({
  '&.tab-0': {
    '.MuiTableHead-root': {
      'th': {
        '&:last-of-type, &:nth-last-of-type(2)': {
          textAlign: 'center',
        },
      }
    }
  },
  '&.tab-1': {
    [theme.breakpoints.up('sm')]: {
      '.MuiTableHead-root': {
        'th': {
          minWidth: '200px'
        }
      },
    },
    '.MuiTableBody-root': {
      'th:first-of-type': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }
    }
  }
})

const StyledSlider = styled(Slider)({
  paddingBottom: '8px',
  '.MuiSlider-rail': {
    backgroundColor: '#dfdfdf',
    height: '2px'
  },
  '.MuiSlider-track': {
    backgroundColor: '#1088ce',
    color: '#1088ce',
    height: '0'
  },
  '.MuiSlider-thumb': {
    backgroundColor: '#ffffff',
    '&:hover': {
      boxShadow: '0px 0px 0px 8px rgb(16 136 206 / 16%)',
    }
  }
})

const StyledBoxVal = styled(Box)({
  padding: '6px 97px 6px 10px',
  border: '1px solid #e4e4e4',
  fontSize: '14px',
  letterSpacing: '0',
  [theme.breakpoints.up('sm')]: {
    padding: '9px 21px 9px 10px',
  }
})

const StyledFormControlLabel = styled(FormControlLabel)({
  margin: '0 0 10px',
  'span': {
    color: '#606060',
    fontSize: '14px',
    letterSpacing: '0',
    lineHeight: 'normal'
  }
})

const StyledCheckbox = styled(Checkbox)({
  padding: '0',
  'svg': {
    width: '24px',
    height: '24px',
    marginRight: '8px'
  }
})

const SingleAccordion: React.FC<SingleAccordionProps> = ({index, accordionData, facets, storeData, handleFilter}) => {
  const [expandedAccordion, setExpandedAccordion] = React.useState(false);

  const handleToggleAccordion = () => {
    setExpandedAccordion(!expandedAccordion)
  }

  return <StyledAccordion
    square={true}
    disableGutters={true}
    key={idFromString(accordionData.accordionTitle)}
  >
    <StyledAccordionSummary onClick={handleToggleAccordion} expandIcon={expandedAccordion ? getIconComponentByName('minus') : getIconComponentByName('plus')} key={idFromString(accordionData.accordionTitle)}>
      <StyledTitle>{accordionData.accordionTitle}</StyledTitle>
    </StyledAccordionSummary>
    <AccordionDetails>
      <StyledTabPanel className={`tab-${index}`} value={index} index={index}>
        <FormGroup>
          {accordionData.accordionCheckbox && accordionData.accordionCheckbox.length > 0 &&
            accordionData.accordionCheckbox.map(mapFacetsToCheckbox.bind(null, facets, storeData, handleFilter))
          }
          {accordionData.accordionSlider &&
            <SliderMarks key={accordionData.accordionSlider.isReset === true ? '0' : '1'} title={accordionData.accordionTitle} valueMin={accordionData.accordionSlider.valueMin} valueMax={accordionData.accordionSlider.valueMax} label={accordionData.accordionSlider.label} marks={accordionData.accordionSlider.marks} marksValues={accordionData.accordionSlider.marksValues} isReset={accordionData.accordionSlider.isReset} storeData={storeData} handleFilter={handleFilter}/>
          }
        </FormGroup>
      </StyledTabPanel>
    </AccordionDetails>
  </StyledAccordion>
}

const mapFacetsToCheckbox = (facets: any, storeData: any, handleFilter: Function, singleRowCheckbox: any, index: number) => {
  let isSelected = false;
  isSelected = facets?.some((facet: any) => { return facet === singleRowCheckbox.value });
  let queryParams : {[key: string]: any} = {
    storeId: storeData.storeId,
    catalogId: storeData.catalogId,
    langId: storeData.langId,
    locale: storeData.locale,
    categoryId: storeData.categoryId,
    pageNumber: storeData.pageNumber,
    pageSize: storeData.pageSize
  }
  return <StyledFormControlLabel key={index} control={<StyledCheckbox checked={isSelected} icon={getIconComponentByName('checkbox')} checkedIcon={getIconComponentByName('checkboxChecked')} value={singleRowCheckbox.value} onChange={e => { queryParams.filterValue = e.target.value; handleFilter(queryParams)} } />} label={singleRowCheckbox.label}/>;
}

export default SingleAccordion;
